'use client'

import { useEffect, useState } from 'react'
import { MdCancel, MdInfoOutline, MdWarning } from 'react-icons/md'

const formatCurrency = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

interface CancelAppointmentModalProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: (reason: string) => Promise<void>
    loading?: boolean
    /** "exame", "consulta", "procedimento", "pacote" — usado nos textos. */
    entityLabel: string
    /**
     * Estado da cobrança vinculada. Decide o aviso mostrado: cobrança paga não é
     * cancelada, ela vira solicitação de estorno — e quem cancela precisa saber
     * disso ANTES de confirmar, não depois.
     */
    billing?: {
        status: string
        amount: number
        paidAmount?: number | null
        refundedAmount?: number | null
    } | null
}

export default function CancelAppointmentModal({
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    entityLabel,
    billing,
}: CancelAppointmentModalProps) {
    const [reason, setReason] = useState('')

    // Reabrir o modal depois de um erro não pode trazer o motivo antigo digitado.
    useEffect(() => {
        if (isOpen) setReason('')
    }, [isOpen])

    if (!isOpen) return null

    const isPaid =
        billing?.status === 'PAID' ||
        billing?.status === 'PARTIALLY_PAID' ||
        billing?.status === 'PARTIALLY_REFUNDED'

    const refundable = billing
        ? Math.max(
              0,
              (billing.paidAmount ?? billing.amount) -
                  (billing.refundedAmount ?? 0)
          )
        : 0

    const hasOpenBilling =
        !!billing &&
        ['PENDING', 'OVERDUE', 'IN_DISPUTE'].includes(billing.status)

    const artigo = entityLabel === 'consulta' ? 'a' : 'o'

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg flex items-center gap-2">
                    <MdCancel className="text-error text-xl" />
                    Cancelar {entityLabel}
                </h3>

                <p className="text-sm text-base-content/70 mt-2">
                    {artigo === 'a' ? 'A' : 'O'} {entityLabel} deixará de
                    constar na agenda e passará a aparecer como{' '}
                    <strong>Cancelad{artigo}</strong> na listagem. O registro
                    não é excluído.
                </p>

                {isPaid && (
                    <div className="alert alert-warning mt-4 items-start">
                        <MdWarning className="text-lg shrink-0 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-semibold">
                                Esta cobrança já foi paga.
                            </div>
                            <p className="mt-1">
                                Será aberta uma{' '}
                                <strong>solicitação de estorno</strong> de{' '}
                                {formatCurrency(refundable)}
                            </p>
                        </div>
                    </div>
                )}

                {hasOpenBilling && (
                    <div className="alert alert-info mt-4 items-start">
                        <MdInfoOutline className="text-lg shrink-0 mt-0.5" />
                        <div className="text-sm">
                            A cobrança vinculada será{' '}
                            <strong>cancelada imediatamente</strong> — o cliente
                            deixa de ser cobrado.
                        </div>
                    </div>
                )}

                <div className="form-control w-full mt-5">
                    <label className="label pb-2" htmlFor="cancel-reason">
                        <span className="label-text font-medium">
                            Motivo do cancelamento
                            {isPaid && <span className="text-error"> *</span>}
                        </span>
                    </label>
                    <textarea
                        id="cancel-reason"
                        className="textarea textarea-bordered w-full h-24 resize-none"
                        placeholder="Ex.: paciente desistiu, remarcado a pedido do profissional..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        maxLength={500}
                        disabled={loading}
                    />
                    <label className="label pt-2">
                        <span className="label-text-alt text-base-content/60">
                            {isPaid
                                ? 'O operador financeiro lerá este motivo antes de aprovar o estorno.'
                                : 'Opcional, mas ajuda no histórico do paciente.'}
                        </span>
                        <span className="label-text-alt text-base-content/40">
                            {reason.length}/500
                        </span>
                    </label>
                </div>

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Voltar
                    </button>
                    <button
                        type="button"
                        className="btn btn-error"
                        // Cobrança paga move dinheiro: o motivo vira a justificativa que o
                        // operador financeiro vai ler para decidir. Sem ele, a fila recebe
                        // um pedido de estorno sem contexto nenhum.
                        disabled={loading || (isPaid && !reason.trim())}
                        onClick={() => onConfirm(reason.trim())}
                    >
                        {loading ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdCancel className="text-base" />
                        )}
                        {isPaid
                            ? 'Cancelar e solicitar estorno'
                            : `Cancelar ${entityLabel}`}
                    </button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose} disabled={loading}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
